// ============================================================================
// Finance view — the tab shell (CW-Q).
//
// Placement, per the brief: INSIDE the existing Dashboard section, as a tab row
// at the top mirroring the My Work pattern, "Executive Overview" first, with
// click-through to the Portfolio.
//
// Role gate: the same set that already gates every money widget on the
// dashboard — finance, executive, superuser — plus the `finance` extra-role hat,
// because that IS the finance role for the user carrying it (Antonela is HR
// primary + finance hat). For anyone else these tabs do not exist and the
// Dashboard renders exactly as it does today.
//
// One fetch feeds both tabs. That is not an optimisation: Page 1 and Page 2 must
// tie to the cent, and the only way to guarantee it is for both to aggregate the
// same rows.
// ============================================================================

import { hasFinanceCapability } from '../../shared/role-gates';
import { useState } from 'react';
import { BarChart3, RefreshCw, Table2 } from 'lucide-react';
import { Button, ErrorState, Skeleton } from '../../shared/components';
import { ExecutiveOverview } from './executive-overview';
import { Portfolio } from './portfolio';
import { REPORTED_MONTH, useFinanceData } from './use-finance-data';
import { monthLabel } from './finance-lib';

export type FinanceTab = 'overview' | 'portfolio';
export type PortfolioFilter = 'all' | 'below_target' | 'needs_fix';

/** Who sees the finance view: has_finance_capability(), the same rule as the
 *  billing history it reads (client_billing_lines). Opens to every employee
 *  with "Every employee reads billing history, invoices and sub payments". */
export function canSeeFinanceView(
  role: string | null,
  extraRoles: readonly string[] | null | undefined,
): boolean {
  return hasFinanceCapability({ role, extra_roles: extraRoles });
}

export function FinanceView({
  tab,
  onTabChange,
}: {
  tab: FinanceTab;
  onTabChange: (t: FinanceTab) => void;
}) {
  const data = useFinanceData(true);
  const [portfolioFilter, setPortfolioFilter] = useState<PortfolioFilter>('all');
  const [cameFromQuestion, setCameFromQuestion] = useState(false);

  const openPortfolio = (filter?: 'below_target' | 'needs_fix') => {
    setPortfolioFilter(filter ?? 'all');
    setCameFromQuestion(filter !== undefined);
    onTabChange('portfolio');
  };

  if (data.error) {
    return (
      <div className="mx-auto max-w-7xl p-4 md:p-6">
        <ErrorState body={data.error} onRetry={data.refresh} />
      </div>
    );
  }

  if (data.loading && data.rows.length === 0) {
    return (
      <div className="mx-auto max-w-7xl space-y-3 p-4 md:p-6">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-end gap-2 px-4 pt-3 md:px-6">
        <span className="mr-auto text-[11px] text-tcs-text-muted">
          {monthLabel(REPORTED_MONTH)} · {data.rows.length} buildings ·{' '}
          {data.lastUpdated
            ? `refreshed ${data.lastUpdated.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })}`
            : 'loading…'}
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={data.refresh}
          disabled={data.loading}
          icon={<RefreshCw className="h-3.5 w-3.5" />}
        >
          Refresh
        </Button>
      </div>
      {tab === 'overview' ? (
        <ExecutiveOverview data={data} onOpenPortfolio={openPortfolio} />
      ) : (
        <Portfolio
          data={data}
          initialFilter={portfolioFilter}
          expandFirst={cameFromQuestion}
        />
      )}
    </div>
  );
}

/** Icons for the Dashboard tab row, kept beside the tab union they describe. */
export const FINANCE_TAB_META: {
  id: FinanceTab;
  label: string;
  icon: typeof BarChart3;
}[] = [
  { id: 'overview', label: 'Executive Overview', icon: BarChart3 },
  { id: 'portfolio', label: 'Portfolio', icon: Table2 },
];
